import { TemplateFormat } from '@langchain/core/prompts';
import { Extension } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view';

import CheckSmallIcon from '../../../icons/CheckSmallIcon.svg?raw';
import VariableIcon from '../../../icons/VariableIcon.svg?raw';
import { TOOLTIP_DATA_ATTRIBUTE, TOOLTIP_ID } from '../constants';
import { isSelectionInsideVariable } from '../utils/isSelectionInVariable';

export const SelectionTooltipExtension = (
  templateFormat: TemplateFormat,
  onVariableCreated: (variableName: string, inputContent: string) => void,
  onBeforeTooltipCreate: () => void
) =>
  Extension.create({
    name: 'selectionTooltip',

    addProseMirrorPlugins() {
      let tooltipElement: HTMLDivElement | null = null;
      let currentDecoration: DecorationSet | null = null;
      let currentFrom: number | null = null;
      let currentTo: number | null = null;
      let globalClickHandler: ((event: MouseEvent) => void) | null = null;

      const closeTooltip = (view: EditorView) => {
        if (tooltipElement) {
          tooltipElement.remove();
          tooltipElement = null;
          currentFrom = null;
          currentTo = null;
          currentDecoration = null;
          const tr = view.state.tr.setMeta('selectionTooltip', 'clear');
          view.dispatch(tr);

          if (globalClickHandler) {
            document.removeEventListener('click', globalClickHandler);
            globalClickHandler = null;
          }
        }
      };

      const handleGlobalClick = (event: MouseEvent, view: EditorView) => {
        const editorElement = view.dom;
        const clickedElement = event.target as Node;

        if (
          tooltipElement &&
          !editorElement.contains(clickedElement) &&
          !tooltipElement.contains(clickedElement)
        ) {
          closeTooltip(view);
        }
      };

      const positionTooltip = (tooltip: HTMLDivElement, view: EditorView) => {
        if (currentFrom !== null) {
          const { top, left } = view.coordsAtPos(currentFrom);
          tooltip.style.top = `${top - tooltip.offsetHeight - 8}px`;
          tooltip.style.left = `${left}px`;
        }
      };

      const createTooltip = (view: EditorView, isFullTooltip = false) => {
        onBeforeTooltipCreate();

        const tooltip = document.createElement('div');
        tooltip.setAttribute(TOOLTIP_DATA_ATTRIBUTE, TOOLTIP_ID);

        if (isFullTooltip) {
          tooltip.className =
            'fixed bg-primary border border-primary rounded-md p-2 shadow-sm z-[1000] flex flex-row gap-2 min-w-[200px]';

          const input = document.createElement('input');
          input.type = 'text';
          input.placeholder = 'Enter variable name';
          input.value = '';
          input.className =
            'px-2 py-0.5 border border-primary rounded-md outline-none text-primary bg-primary focus:border-brand text-sm';

          input.addEventListener('input', (e) => {
            const target = e.target as HTMLInputElement;
            target.value = target.value.replace(/\s+/g, '');
          });

          const convertButton = document.createElement('button');
          convertButton.innerHTML = CheckSmallIcon;
          convertButton.className =
            'px-1 py-1 bg-brand-primary text-button-primary border-none rounded-md cursor-pointer transition-colors hover:bg-brand-primary-hover text-sm';

          const handleConvert = () => {
            const variableName = input.value.trim() || 'variableName';
            const variableText =
              templateFormat === 'f-string'
                ? `{${variableName}}`
                : `{{${variableName}}}`;

            if (currentFrom !== null && currentTo !== null) {
              onVariableCreated(
                variableName,
                view.state.doc.textBetween(currentFrom, currentTo)
              );
            }

            view.dispatch(
              view.state.tr.replaceWith(
                currentFrom!,
                currentTo!,
                view.state.schema.text(variableText)
              )
            );

            closeTooltip(view);
          };

          convertButton.onclick = handleConvert;

          input.onkeydown = (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleConvert();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              closeTooltip(view);
            }
          };

          tooltip.appendChild(input);
          tooltip.appendChild(convertButton);

          setTimeout(() => {
            input.focus();
            input.select();
          }, 0);
        } else {
          tooltip.className =
            'fixed bg-primary border border-primary rounded-full p-1 shadow-sm z-[1000] flex items-center hover:bg-primary-hover';

          const button = document.createElement('button');

          button.innerHTML = VariableIcon;
          button.className = 'p-0.5 text-primary rounded-full relative';

          const tooltipEl = document.createElement('div');
          tooltipEl.textContent = 'Convert to variable';
          tooltipEl.className =
            'absolute hidden border border-primary bg-black dark:bg-white text-white dark:text-black px-2 py-1 rounded text-xxs whitespace-nowrap -top-9 left-1/2 -translate-x-1/2';
          button.appendChild(tooltipEl);

          button.addEventListener('mouseenter', () =>
            tooltipEl.classList.remove('hidden')
          );
          button.addEventListener('mouseleave', () =>
            tooltipEl.classList.add('hidden')
          );

          button.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            createTooltip(view, true);
          };

          tooltip.appendChild(button);
        }

        tooltipElement = tooltip;
        document.body.appendChild(tooltip);

        globalClickHandler = (e: MouseEvent) => handleGlobalClick(e, view);
        document.addEventListener('click', globalClickHandler);

        positionTooltip(tooltip, view);
      };

      return [
        new Plugin({
          state: {
            init() {
              return null;
            },
            apply(tr, old) {
              const meta = tr.getMeta('selectionTooltip');
              if (meta === 'clear') {
                currentFrom = null;
                currentTo = null;
                return null;
              }
              return meta || old;
            },
          },
          props: {
            decorations(state) {
              return this.getState(state);
            },
            handleDOMEvents: {
              mouseup: (view) => {
                const { state } = view;
                const { selection } = state;
                const { empty } = selection;

                if (empty) {
                  closeTooltip(view);
                  return false;
                }

                if (!onVariableCreated) {
                  return false;
                }

                closeTooltip(view);

                const { from, to } = selection;
                const docText = view.state.doc.textBetween(
                  0,
                  view.state.doc.content.size
                );
                // Check if either the start or end of the selection is within a template variable
                if (
                  isSelectionInsideVariable(from, to, docText, templateFormat)
                ) {
                  return false;
                }

                currentFrom = from;
                currentTo = to;

                const decoration = Decoration.inline(from, to, {
                  class: 'bg-brand-secondary',
                });
                currentDecoration = DecorationSet.create(view.state.doc, [
                  decoration,
                ]);
                const tr = view.state.tr.setMeta(
                  'selectionTooltip',
                  currentDecoration
                );
                view.dispatch(tr);

                createTooltip(view, false);

                return true;
              },
              keydown: (view) => {
                closeTooltip(view);
                return false;
              },
              mousedown: (view, event) => {
                if (
                  tooltipElement &&
                  !tooltipElement.contains(event.target as Node)
                ) {
                  closeTooltip(view);
                }
                return false;
              },
              blur: (view, event) => {
                if (
                  event?.relatedTarget &&
                  tooltipElement?.contains(event.relatedTarget as Node)
                ) {
                  return false;
                }
                closeTooltip(view);
                return false;
              },
            },
          },
          destroy() {
            if (tooltipElement) {
              tooltipElement.remove();
              tooltipElement = null;
            }
          },
        }),
      ];
    },
  });
